import { useState } from "react";
import { differenceInCalendarWeeks, format, isToday, isYesterday } from "date-fns";
import { Bell } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { patchListCache, replaceInList } from "@trainrr/api-client";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useOrg } from "@/lib/states/session/hooks";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import { DEFAULT_WEEK_STARTS_ON, type WeekStartsOn } from "@/lib/dates/weekStart";
import { trpc } from "@/lib/trpc/client";
import {
  parseNotificationData,
  resolveNotificationNavigation,
} from "@/lib/utils/notificationUtils";
import { store } from "@/lib/states/store";

export type NotificationItem = {
  id: string;
  title: string | null;
  body: string | null;
  created_at: string;
  viewed: boolean;
  data?: unknown | null;
};

export const groupNotifications = (
  notifications: NotificationItem[],
  weekStartsOn: WeekStartsOn = DEFAULT_WEEK_STARTS_ON,
) => {
  const groups = {
    Today: [] as NotificationItem[],
    Yesterday: [] as NotificationItem[],
    "This week": [] as NotificationItem[],
    "Last week": [] as NotificationItem[],
    Older: [] as NotificationItem[],
  };

  const now = new Date();
  notifications.forEach((notification) => {
    const createdAt = new Date(notification.created_at);
    if (isToday(createdAt)) {
      groups.Today.push(notification);
      return;
    }
    if (isYesterday(createdAt)) {
      groups.Yesterday.push(notification);
      return;
    }
    const weekDiff = differenceInCalendarWeeks(now, createdAt, {
      weekStartsOn,
    });
    if (weekDiff === 0) {
      groups["This week"].push(notification);
      return;
    }
    if (weekDiff === 1) {
      groups["Last week"].push(notification);
      return;
    }
    groups.Older.push(notification);
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
};

const NotificationBell = () => {
  const org = useOrg();
  const weekStartsOn = useWeekStartsOn();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();
  const markReadMutation = trpc.notifications.markRead.useMutation();
  const markAllReadMutation = trpc.notifications.markAllRead.useMutation();

  const { data: unreadCount = 0 } = trpc.notifications.unreadCount.useQuery(
    undefined,
    {
      enabled: Boolean(org?.id),
    },
  );
  const { data: notifications } = trpc.notifications.list.useQuery(undefined, {
    enabled: open && Boolean(org?.id),
  });

  const patchUnreadCount = (updater: (current: number) => number) => {
    const current = utils.notifications.unreadCount.getData();
    if (current == null) {
      void utils.notifications.unreadCount.invalidate();
      return;
    }
    utils.notifications.unreadCount.setData(undefined, () => updater(current));
  };

  const markRead = async (id: string) => {
    try {
      const updated = await markReadMutation.mutateAsync({ id });
      if (!updated) {
        return;
      }
      patchListCache(utils.notifications.list, (old) =>
        replaceInList(old, updated),
      );
      patchUnreadCount((current) => Math.max(0, current - 1));
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllReadMutation.mutateAsync();
      patchListCache(utils.notifications.list, (old) =>
        old.map((notification) => ({ ...notification, viewed: true })),
      );
      const current = utils.notifications.unreadCount.getData();
      if (current == null) {
        void utils.notifications.unreadCount.invalidate();
      } else {
        utils.notifications.unreadCount.setData(undefined, () => 0);
      }
    } catch (error) {
      console.error("Failed to mark notifications as read", error);
    }
  };

  const handleClick = async (notification: NotificationItem) => {
    const parsedData = parseNotificationData(notification.data);

    if (!notification.viewed) {
      await markRead(notification.id);
    }

    if (parsedData?.type === "coach_app_launch") {
      store.getState().coachAppDialog.openDialog();
      setOpen(false);
      return;
    }

    if (parsedData?.type === "task_assigned") {
      store.getState().tasksSheet.openSheet(parsedData.taskId);
      setOpen(false);
      return;
    }

    const navigation = await resolveNotificationNavigation(
      notification.data,
    );
    if (navigation) {
      navigate(navigation);
    }
    setOpen(false);
  };

  const grouped = groupNotifications(notifications ?? [], weekStartsOn);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8"
          data-ph-capture-attribute-unread-notifications-count={unreadCount}
          data-analytics="notification-bell"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-xs font-semibold text-destructive-foreground">
              {unreadCount}
            </span>
          )}
          <span className="sr-only">View notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="border-b px-3 py-2 text-sm font-medium flex items-center justify-between">
          <span>Notifications</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {notifications?.length ? (
            grouped.map((group) => (
              <div key={group.label} className="border-b last:border-b-0">
                <div className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
                  {group.label}
                </div>
                <div>
                  {group.items.map((notification) => (
                    <button
                      key={notification.id}
                      type="button"
                      className="w-full border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted/40"
                      onClick={() => handleClick(notification)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">
                          {notification.title ?? "Notification"}
                        </div>
                        {!notification.viewed && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-blue-600" />
                        )}
                      </div>
                      {notification.body && (
                        <div className="text-xs text-muted-foreground">
                          {notification.body}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(notification.created_at), "MMM do, p")}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              No notifications yet.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
